import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import '../../styles/components.css';
import { fetchAuth } from '../../utils/api';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend
);

function DashboardView({ setView, handleApiError, showNotification, user }) {
    const [summary, setSummary] = useState({ totalLeads: 0, totalCustomers: 0, totalOrders: 0, totalRevenue: 0 });
    const [chartData, setChartData] = useState(null);
    const [todaysTasks, setTodaysTasks] = useState([]);
    const [recentActivity, setRecentActivity] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [summaryRes, tasksRes, activityRes] = await Promise.all([
                    fetchAuth(`/api/dashboard/summary`),
                    fetchAuth(`/api/dashboard/today-tasks`),
                    fetchAuth(`/api/dashboard/recent-activity`)
                ]);

                if (summaryRes.ok) {
                    const data = await summaryRes.json();
                    setSummary(data);
                }
                if (tasksRes.ok) setTodaysTasks(await tasksRes.json());
                if (activityRes.ok) setRecentActivity(await activityRes.json());

                const chartRes = await fetchAuth(`/api/dashboard/revenue-chart`);
                if (chartRes.ok) {
                    const chartDataResponse = await chartRes.json();
                    setChartData({
                        labels: chartDataResponse.labels.map(d => new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })),
                        datasets: [{
                            data: chartDataResponse.data,
                            label: 'Daily Revenue',
                            fill: true,
                            borderColor: '#6366f1',
                            tension: 0.4,
                            pointBackgroundColor: '#6366f1',
                            pointBorderColor: '#ffffffff',
                            pointRadius: 5,
                            pointHoverRadius: 7
                        }]
                    });
                }
            } catch (err) { handleApiError(err); }
        };
        fetchData();
    }, [handleApiError]);

    const summaryCards = [
        { title: 'Total Leads', value: summary.totalLeads, icon: 'M12 4c-4.41 0-8 3.59-8 8s3.59 8 8 8 8-3.59 8-8-3.59-8-8-8zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 10.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z', color: '#3b82f6' },
        { title: 'Total Customers', value: summary.totalCustomers, icon: 'M16.5 12c1.38 0 2.5-1.12 2.5-2.5S17.88 7 16.5 7 14 8.12 14 9.5s1.12 2.5 2.5 2.5zM9 11c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3zm7.5 3c-1.83 0-5.5.92-5.5 2.75V18h11v-1.25c0-1.83-3.67-2.75-5.5-2.75zM9 13c-2.33 0-7 1.17-7 3.5V18h7v-1.5c0-.85.33-2.34 2.37-3.41C10.5 13.1 9.66 13 9 13z', color: '#10b981' },
        { title: 'Total Orders', value: summary.totalOrders, icon: 'M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49A1.003 1.003 0 0020 4H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z', color: '#a855f7' },
        { title: 'Total Revenue', value: `₹${Number(summary.totalRevenue || 0).toLocaleString('en-IN')}`, icon: 'M5 20h14v-2H5v2zM19 4H5v10h14V4zM5 2h14c1.1 0 2 .9 2 2v10c0 1.1-.9 2-2 2H5c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2z', color: '#f59e0b' },
    ];

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false }
        },
        scales: {
            x: {
                grid: {
                    display: false
                }
            },
            y: {
                ticks: {
                    callback: (value) => `₹${value}`
                },
                grid: {
                    display: true
                }
            }
        }
    };

    const ActivityIcon = ({ type }) => {
        const icons = {
            SALE_CREATED: { path: 'M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49A1.003 1.003 0 0020 4H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z', color: '#10b981' },
            LEAD_CREATED: { path: 'M12 4c-4.41 0-8 3.59-8 8s3.59 8 8 8 8-3.59 8-8-3.59-8-8-8zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 10.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z', color: '#3b82f6' },
            TASK_CREATED: { path: 'M6 14h12v-2H6v2zm0-4h12V8H6v2zm0-4h12V4H6v2z', color: '#a855f7' },
        };
        const icon = icons[type] || icons['LEAD_CREATED'];
        return <div className="activity-icon-v2" style={{ backgroundColor: icon.color }}><svg viewBox="0 0 24 24"><path d={icon.path} fill="currentColor"></path></svg></div>
    };

    return (
        <div className="dashboard-page">
            <div className="page-header">
                <h2 className="page-title">Dashboard</h2>
                <button className="btn-secondary">Customize</button>
            </div>
            <div className="summary-cards-grid">
                {summaryCards.map(card => (
                    <div key={card.title} className="summary-card-v2">
                        <div className="card-info">
                            <p>{card.title}</p>
                            <h3>{card.value}</h3>
                        </div>
                        <div className="card-icon-v2" style={{ backgroundColor: card.color + '20', color: card.color }}>
                            <svg viewBox="0 0 24 24"><path d={card.icon} fill="currentColor"></path></svg>
                        </div>
                    </div>
                ))}
            </div>
            <div className="dashboard-main-content">
                <div className="chart-container-v2">
                    <h3>Revenue Overview (Last 30 Days)</h3>
                    <div className="chart-wrapper">{chartData ? <Line options={chartOptions} data={chartData} /> : <p>Loading chart...</p>}</div>
                </div>
                <div className="dashboard-sidebar">
                    <div className="tasks-card">
                        <h3>My Tasks for Today</h3>
                        <ul>{todaysTasks.length > 0 ? todaysTasks.map(task => <li key={task.id}>{task.title}</li>) : <li>No tasks due today.</li>}</ul>
                    </div>
                    <div className="activity-card">
                        <h3>Recent Activity</h3>
                        <ul>{recentActivity.length > 0 ? recentActivity.map((act, i) => <li key={i + act.date}><ActivityIcon type={act.type} /><span>{act.text}</span></li>) : <li>No recent activity.</li>}</ul>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default DashboardView;
